<template>
    <div class="ali-phone-vert-dialog" v-show="visible">
        <header>
            <div class="title">虚拟号码拨打人员实名认证</div>
            <div class="close-box" @click="closeAction"><md-icon><close /></md-icon></div>
        </header>

        <section class="content-box">
            应工信部要求，所有拨打虚拟号码的用户，必须在指定的链接进行实名备案；否则未实名认证的手机号码自4月25日起将无法打通虚拟号码。链接如下：
            <a href="https://cmccsign.com/identity_trust/#/login?companyTransactionId=fbcad111ad7d402abb8425010fcf815c&platformId=1733818705992" target="_blank"> https://cmccsign.com/identity_trust/#/login?companyTransactionId=fbcad111ad7d402abb8425010fcf815c&platformId=1733818705992 </a>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { Close } from '@mdesign3/icons-vue';
import request from '@/utils/http';
import { setLocal, getLocal } from '@/utils/storage';
import dayjs from 'dayjs';

// Define emits
const emit = defineEmits(['enableChange']);

// Constants
const PAGES = [
    '/microLsp/otp/appointment-center', // 预约中心 lsp系统
    '/microLsp/otp/dispatch-center', // 调度中心 lsp系统
    '/microLsp/otp/receipt-center', // 回单签收 lsp系统
    '/microTms/dispatch-Manage/appointment-center', // 预约中心 otp系统
    '/microTms/dispatch-Manage/new-dispatch-center', // 调度中心（新）otp系统
    '/microTms/dispatch-Manage/dispatch-center-line', // 调度中心（线路） otp系统
    '/microTms/dispatch-Manage/algorithm-dispatch', // 调度中心（算法）otp系统
    '/microTms/performance/receipt-center' // 签收中心 otp系统
];

// Store and Route
const store = useStore();
const route = useRoute();

// Reactive State
const visible = ref(false);
const isExpired = ref(true);

// Computed Properties
const user = computed(() => store.getters.user);
const enable = computed(() => {
    return PAGES.includes(route.path) && !isExpired.value;
});
const LOCAL_KEY = computed(() => {
    const isLSP = route.path.includes('microLsp');
    return isLSP ? '__LSP_ALI_PHONE_VERT_DATE__' : '__OTP_ALI_PHONE_VERT_DATE__';
});

// Methods
const closeAction = () => {
    visible.value = false;
};

const show = () => {
    visible.value = true;
};

const toCache = () => {
    if (!user.value?.userCode) return; // Guard against missing userCode
    const cache = getLocal(LOCAL_KEY.value) || {};
    cache[user.value.userCode] = dayjs().format('YYYY-MM-DD');
    setLocal(LOCAL_KEY.value, cache);
};

/** 获取字典配置 */
const getDictConfig = async (): Promise<{ lastDay?: string }> => {
    try {
        const dictCode = 'OTP_ALI_PHONE_VERT_DATE';
        // 使用类型断言 'as any' 来绕过类型检查
        const res = await request<{ data?: { [key: string]: { lastDay?: string } } }>({
            url: '/api-mdm/dictionaryDetail/dict',
            method: 'get',
            params: { dictCodes: dictCode },
            showProgress: false // 添加此行以禁用 NProgress
        } as any); // 添加类型断言

        // 检查 res 和 res.data 是否存在，并确保 data 是一个对象
        if (res && res.data && typeof res.data === 'object') {
            const data = res.data;
            // 现在可以安全地使用 'in' 操作符
            if (dictCode in data) {
                // 使用类型断言明确告知 TypeScript 类型
                return data[dictCode] as { lastDay?: string };
            }
        }
    } catch (err) {
    }
    return {};
};

const updateExpired = async () => {
    const { lastDay } = await getDictConfig();
    isExpired.value = lastDay ? dayjs().isAfter(dayjs(lastDay), 'day') : false;
};

const checkAndShow = async () => {
    if (!enable.value || !user.value?.userCode) return;

    // 1、检查是否已经提示过；
    const cache = getLocal(LOCAL_KEY.value) || {};
    const oldDay = cache[user.value.userCode]; // 上次提示的日期
    const curDay = dayjs();

    const isShowed = oldDay ? curDay.isSame(oldDay, 'day') || curDay.isBefore(oldDay, 'day') : false;
    if (isShowed) return;

    show();
    toCache();
};

watch(enable, (newVal) => {
    emit('enableChange', newVal);
});

watch(() => route.path, (newPath) => {
    closeAction();
    if (PAGES.includes(newPath)) {
        isExpired.value = true;
        updateExpired().then(checkAndShow);
    } else {
        emit('enableChange', false);
    }
}, { immediate: true });

defineExpose({ show });

</script>

<style lang="less" scoped>
.ali-phone-vert-dialog {
    position: fixed;
    top: 40px;
    right: 40px;
    width: 360px;
    min-height: 220px;
    line-height: 1.5;
    z-index: 199999999;
    background-color: #fff;
    border: 1px solid #e6e6e6;
    header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border: 1px solid #e6e6e6;
        padding: 12px 20px;
        .title {
            line-height: 24px;
            font-size: 16px;
            color: #252626;
        }
    }

    .close-box {
        width: 20px;
        height: 20px;
        font-size: 16px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .content-box {
        font-size: 14px;
        padding: 16px;
        color: #555757;
        word-break: break-all;
        a {
            color: #0077ff;
            &:hover {
                text-decoration: underline;
            }
        }
    }
}
</style>
